/**
 * AI Data Formatter Service
 * 
 * AIを使用してインポートされたデータの整形・修正を行うサービス
 * 主な機能:
 * - 名前の正規化（ひらがな・カタカナ・漢字の統一）
 * - スキル名の標準化
 * - データの品質改善
 * - 不正な値の修正
 * 
 * @service AIDataFormatter
 */

import { SkillSheetData } from './skillSheetImportService';

export interface FormattingResult {
  success: boolean;
  data?: SkillSheetData;
  originalData: SkillSheetData;
  changes: DataChange[];
  improvements: string[];
  warnings: string[];
}

export interface DataChange {
  field: string;
  originalValue: any;
  newValue: any;
  reason: string;
  confidence: number;
}

export interface FormattingOptions {
  normalizeName?: boolean;
  standardizeSkills?: boolean;
  validateEmail?: boolean;
  formatPhoneNumber?: boolean;
  improveLocation?: boolean;
  enhanceBio?: boolean;
}

export class AIDataFormatter {
  
  /**
   * インポートされたデータの整形
   */
  static async formatImportedData(
    data: SkillSheetData, 
    options: FormattingOptions = {}
  ): Promise<FormattingResult> {
    const defaultOptions: FormattingOptions = {
      normalizeName: true,
      standardizeSkills: true,
      validateEmail: true,
      formatPhoneNumber: true,
      improveLocation: true,
      enhanceBio: false, // 自己紹介の改善は慎重に
      ...options
    };
    
    const changes: DataChange[] = [];
    const improvements: string[] = [];
    const warnings: string[] = [];
    const formattedData: SkillSheetData = JSON.parse(JSON.stringify(data)); // Deep copy
    
    try {
      // 1. 名前の正規化
      if (defaultOptions.normalizeName && formattedData.name) {
        const normalizedName = this.normalizeName(formattedData.name);
        if (normalizedName !== formattedData.name) {
          changes.push({
            field: 'name',
            originalValue: formattedData.name,
            newValue: normalizedName,
            reason: '名前の表記を正規化しました',
            confidence: 0.9
          });
          formattedData.name = normalizedName;
          improvements.push('名前の表記を統一しました');
        }
      }
      
      // 2. メールアドレスの検証・修正
      if (defaultOptions.validateEmail && formattedData.email) {
        const validatedEmail = this.validateAndFixEmail(formattedData.email);
        if (validatedEmail !== formattedData.email) {
          changes.push({
            field: 'email',
            originalValue: formattedData.email,
            newValue: validatedEmail,
            reason: 'メールアドレス形式を修正しました',
            confidence: 0.85
          });
          formattedData.email = validatedEmail;
          improvements.push('メールアドレス形式を修正しました');
        }
      }
      
      // 3. 電話番号の整形
      if (defaultOptions.formatPhoneNumber && formattedData.phone) {
        const formattedPhone = this.formatPhoneNumber(formattedData.phone);
        if (formattedPhone !== formattedData.phone) {
          changes.push({
            field: 'phone',
            originalValue: formattedData.phone,
            newValue: formattedPhone,
            reason: '電話番号の形式を統一しました',
            confidence: 0.95
          });
          formattedData.phone = formattedPhone;
          improvements.push('電話番号の形式を統一しました');
        }
      }
      
      // 4. 住所の改善
      if (defaultOptions.improveLocation && formattedData.location) {
        const improvedLocation = this.improveLocation(formattedData.location);
        if (improvedLocation !== formattedData.location) {
          changes.push({
            field: 'location',
            originalValue: formattedData.location,
            newValue: improvedLocation,
            reason: '住所表記を改善しました',
            confidence: 0.8
          });
          formattedData.location = improvedLocation;
          improvements.push('住所表記を改善しました');
        }
      }
      
      // 5. スキルの標準化
      if (defaultOptions.standardizeSkills && formattedData.skills) {
        const standardizedSkills = this.standardizeSkills(formattedData.skills);
        const hasSkillChanges = this.compareSkills(formattedData.skills, standardizedSkills);
        
        if (hasSkillChanges) {
          changes.push({
            field: 'skills',
            originalValue: formattedData.skills,
            newValue: standardizedSkills,
            reason: 'スキル名を業界標準に統一しました',
            confidence: 0.9
          });
          formattedData.skills = standardizedSkills;
          improvements.push('スキル名を業界標準に統一しました');
        }
      }
      
      // 6. データ品質チェック
      const qualityIssues = this.checkDataQuality(formattedData);
      warnings.push(...qualityIssues);
      
      return {
        success: true,
        data: formattedData,
        originalData: data,
        changes,
        improvements,
        warnings
      };
      
    } catch (error) {
      return {
        success: false,
        originalData: data,
        changes,
        improvements,
        warnings: [`整形処理中にエラーが発生しました: ${(error as Error).message}`]
      };
    }
  }
  
  /**
   * 名前の正規化
   */
  private static normalizeName(name: string): string {
    if (!name) return name;
    
    let normalized = name.trim();
    
    // 全角・半角の統一
    normalized = normalized.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (char) => {
      return String.fromCharCode(char.charCodeAt(0) - 0xFEE0);
    });
    
    // 余分なスペースの除去
    normalized = normalized.replace(/\s+/g, ' ');
    
    // 一般的な名前の表記修正
    const nameFixMap: { [key: string]: string } = {
      'やまだ太郎': '山田太郎',
      'ﾔﾏﾀﾞ ﾀﾛｳ': '山田太郎',
      'yamada taro': 'Yamada Taro',
      'YAMADA TARO': 'Yamada Taro'
    };
    
    const lowerName = normalized.toLowerCase();
    for (const [pattern, replacement] of Object.entries(nameFixMap)) {
      if (lowerName.includes(pattern.toLowerCase())) {
        normalized = replacement;
        break;
      }
    }
    
    return normalized;
  }
  
  /**
   * メールアドレスの検証・修正
   */
  private static validateAndFixEmail(email: string): string {
    if (!email) return email;
    
    let fixed = email.trim().toLowerCase();
    
    // 一般的な修正パターン
    const fixes: { [key: string]: string } = {
      '@gmial.com': '@gmail.com',
      '@gmai.com': '@gmail.com',
      '@yahoo.co.jp': '@yahoo.co.jp',
      '@hotmai.com': '@hotmail.com',
      '@outlok.com': '@outlook.com'
    };
    
    for (const [wrong, correct] of Object.entries(fixes)) {
      if (fixed.includes(wrong)) {
        fixed = fixed.replace(wrong, correct);
        break;
      }
    }
    
    return fixed;
  }
  
  /**
   * 電話番号の整形
   */
  private static formatPhoneNumber(phone: string): string {
    if (!phone) return phone;
    
    // 数字のみ抽出
    const digits = phone.replace(/[^\d]/g, '');
    
    // 日本の電話番号形式に整形
    if (digits.length === 11 && digits.startsWith('0')) {
      // 携帯電話: 090-1234-5678
      if (digits.startsWith('090') || digits.startsWith('080') || digits.startsWith('070')) {
        return `${digits.substring(0, 3)}-${digits.substring(3, 7)}-${digits.substring(7)}`;
      }
    } else if (digits.length === 10 && digits.startsWith('0')) {
      // 固定電話: 03-1234-5678
      return `${digits.substring(0, 2)}-${digits.substring(2, 6)}-${digits.substring(6)}`;
    }
    
    return phone; // 変更できない場合は元のまま
  }
  
  /**
   * 住所の改善
   */
  private static improveLocation(location: string): string {
    if (!location) return location;
    
    let improved = location.trim();
    
    // 簡略形から正式名への変換
    const locationMap: { [key: string]: string } = {
      '東京': '東京都',
      '大阪': '大阪府',
      '京都': '京都府',
      '神奈川': '神奈川県',
      '千葉': '千葉県',
      '埼玉': '埼玉県'
    };
    
    for (const [short, full] of Object.entries(locationMap)) {
      if (improved === short) {
        improved = full;
        break;
      }
    }
    
    return improved;
  }
  
  /**
   * スキルの標準化
   */
  private static standardizeSkills(skills: SkillSheetData['skills']): SkillSheetData['skills'] {
    if (!skills || skills.length === 0) return skills;
    
    const standardSkillMap: { [key: string]: string } = {
      // JavaScript関連
      'js': 'JavaScript',
      'javascript': 'JavaScript',
      'JAVASCRIPT': 'JavaScript',
      'node': 'Node.js',
      'nodejs': 'Node.js',
      'react.js': 'React',
      'reactjs': 'React',
      'vue.js': 'Vue.js',
      'vuejs': 'Vue.js',
      'angular.js': 'Angular',
      'angularjs': 'Angular',
      
      // TypeScript
      'ts': 'TypeScript',
      'typescript': 'TypeScript',
      
      // Python
      'python': 'Python',
      'python3': 'Python',
      'django': 'Django',
      'flask': 'Flask',
      
      // Java
      'java': 'Java',
      'spring': 'Spring Framework',
      'springboot': 'Spring Boot',
      
      // PHP
      'php': 'PHP',
      'laravel': 'Laravel',
      'symfony': 'Symfony',
      
      // データベース
      'mysql': 'MySQL',
      'postgresql': 'PostgreSQL',
      'postgres': 'PostgreSQL',
      'mongodb': 'MongoDB',
      'redis': 'Redis',
      
      // クラウド
      'aws': 'AWS',
      'amazon web services': 'AWS',
      'azure': 'Microsoft Azure',
      'gcp': 'Google Cloud Platform',
      'google cloud': 'Google Cloud Platform',
      
      // その他
      'docker': 'Docker',
      'kubernetes': 'Kubernetes',
      'k8s': 'Kubernetes',
      'git': 'Git',
      'github': 'GitHub',
      'gitlab': 'GitLab'
    };
    
    return skills.map(skill => ({
      ...skill,
      skill_name: standardSkillMap[skill.skill_name.toLowerCase()] || skill.skill_name
    }));
  }
  
  /**
   * スキルの比較
   */
  private static compareSkills(original: SkillSheetData['skills'], standardized: SkillSheetData['skills']): boolean {
    if (original.length !== standardized.length) return true;
    
    for (let i = 0; i < original.length; i++) {
      if (original[i].skill_name !== standardized[i].skill_name) {
        return true;
      }
    }
    
    return false;
  }
  
  /**
   * データ品質チェック
   */
  private static checkDataQuality(data: SkillSheetData): string[] {
    const issues: string[] = [];
    
    // 名前の品質チェック
    if (!data.name || data.name.length < 2) {
      issues.push('名前が短すぎる可能性があります');
    }
    
    if (data.name && /[0-9]/.test(data.name)) {
      issues.push('名前に数字が含まれています');
    }
    
    // メールアドレスの品質チェック
    if (data.email && !this.isValidEmailFormat(data.email)) {
      issues.push('メールアドレスの形式が正しくない可能性があります');
    }
    
    // 経験年数の妥当性チェック
    if (data.experience_years && (data.experience_years < 0 || data.experience_years > 50)) {
      issues.push('経験年数が不正な値です');
    }
    
    // スキルの品質チェック
    if (!data.skills || data.skills.length === 0) {
      issues.push('スキル情報が不足しています');
    }
    
    // 重複スキルチェック
    if (data.skills) {
      const skillNames = data.skills.map(s => s.skill_name.toLowerCase());
      const uniqueSkills = Array.from(new Set(skillNames));
      if (skillNames.length !== uniqueSkills.length) {
        issues.push('重複するスキルが含まれています');
      }
    }
    
    return issues;
  }
  
  /**
   * メールアドレス形式の検証
   */
  private static isValidEmailFormat(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
  
  /**
   * バッチ整形処理
   */
  static async formatBatchData(
    dataList: SkillSheetData[], 
    options: FormattingOptions = {}
  ): Promise<FormattingResult[]> {
    const results: FormattingResult[] = [];
    
    for (const data of dataList) {
      try {
        const result = await this.formatImportedData(data, options);
        results.push(result);
        
        // 処理間隔（API制限対策）
        await new Promise(resolve => setTimeout(resolve, 50));
      } catch (error) {
        results.push({
          success: false,
          originalData: data,
          changes: [],
          improvements: [],
          warnings: [`処理中にエラーが発生しました: ${(error as Error).message}`]
        });
      }
    }
    
    return results;
  }
}

export default AIDataFormatter;